import { useEffect, useMemo, useRef, useState } from 'react';
import { EventType, PlayerSummary, RealtimeMessage } from '@inplay/models';
import { PlayerService } from '../service/playerService';
import { errorToast, validateAndDecodeAuthToken } from '../utils/helper';
import { PlayerPageLimit } from '../utils/helper.type';
import { AuthTokenResponse } from '../service/service.type';
import { useLocation } from 'react-router-dom';
import { route } from '../service/router';
import { useWebSocket } from '../service/context/websocketContext';

export const usePlayerSummary = (
  venueBranchIds: string[],
  startTime?: string,
  endTime?: string,
  playerPageNumber?: number, // send undefined to get all players, we can’t get all players in 1 go because of the http response size due to base64 encoded images
  limit?: number,
  onlyCurrentPlayers?: boolean,
): {
  playerSummary: PlayerSummary[];
  allPlayers: PlayerSummary[];
  playerLoading: boolean;
  decodedLoginData: AuthTokenResponse | null;
} => {
  const [playerSummary, setPlayerSummary] = useState<PlayerSummary[]>([]);
  const [allPlayers, setAllPlayers] = useState<PlayerSummary[]>([]);
  const [playerLoading, setPlayerLoading] = useState<boolean>(true);
  const [decodedLoginData, setDecodedLoginData] =
    useState<AuthTokenResponse | null>(null);

  const location = useLocation();
  const isSweepRoute: boolean = location.pathname.includes(route.sweeps);

  const venueBranchDependencyKey: string = useMemo(() => {
    return isSweepRoute ? 'ignore' : JSON.stringify(venueBranchIds);
  }, [isSweepRoute, venueBranchIds]);

  const websocket = useWebSocket();
  const visitDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Add onMessage events from Websocket here
  useEffect(() => {
    if (!venueBranchIds || !venueBranchIds.length) {
      console.log(
        '[usePlayerSummary] Skipping initialization — missing venueBranchIds',
      );
      return;
    }
    console.log('[usePlayerSummary] websocket useEffect ENTRY');
    websocket.service.onMessage((data: RealtimeMessage) => {
      console.log('[usePlayerSummary] ENTRY');
      switch (data.eventType) {
        // Visit events
        case 'visit':
          console.log(`[usePlayerSummary][websocket][visit]`, data.eventType);
          if (visitDebounceRef.current) clearTimeout(visitDebounceRef.current);
          visitDebounceRef.current = setTimeout(() => {
            fetchPlayerData(true);
            fetchAllPlayersWithPagination(true);
          }, 500);
          break;
        // Add more cases as needed
        default:
          console.warn(
            `Unhandled WebSocket event: ${data.eventType}`,
            data.message,
          );
      }
    });
  }, [websocket, venueBranchIds.length]);

  useEffect(() => {
    const decodedData: AuthTokenResponse | null = validateAndDecodeAuthToken();
    if (decodedData) {
      setDecodedLoginData(decodedData);
    }
  }, []);

  useEffect(() => {
    const decodedData: AuthTokenResponse | null = validateAndDecodeAuthToken();
    console.log('venueBranchIds useEffect', {
      decodedData: !!decodedData,
      venueBranchIds,
      isConnected: websocket.isConnected,
    });

    if (decodedData && venueBranchIds.length > 0 && websocket.isConnected) {
      console.log('Sending WebSocket message - connection ready', {
        decodedData,
        venueBranchIds,
      });
      websocket.service.sendMessage({
        eventType: EventType.VISIT,
        organizationUnitId: venueBranchIds[0],
        userId: decodedData.sub,
      });
    } else if (
      decodedData &&
      venueBranchIds.length > 0 &&
      !websocket.isConnected
    ) {
      console.log('Waiting for WebSocket connection...');
    }
  }, [venueBranchIds.length, websocket.isConnected]);

  const getFormattedPlayersPage = async (
    page: number,
    pageLimit: number,
  ): Promise<PlayerSummary[]> => {
    const playerService: PlayerService = new PlayerService();
    const rawPlayers: PlayerSummary[] = await playerService.getPlayerSummaries(
      venueBranchIds,
      startTime ? parseInt(startTime) : undefined,
      endTime ? parseInt(endTime) : undefined,
      page,
      pageLimit,
      onlyCurrentPlayers === undefined ? true : onlyCurrentPlayers,
    );

    return rawPlayers.map((player) => ({
      ...player,
      image: player.fullImage || '',
      faceImage: player.faceImage || '',
    }));
  };

  const fetchPlayerData = async (fromSocket?: boolean) => {
    if (!fromSocket) setPlayerLoading(true);
    try {
      const page: number = playerPageNumber ?? 0;
      const limitPerPage: number = limit ?? PlayerPageLimit.LIMIT;

      const players: PlayerSummary[] = await getFormattedPlayersPage(
        page,
        limitPerPage,
      );

      const sorted: PlayerSummary[] = [
        ...(page === 0 ? [] : playerSummary),
        ...players,
      ].sort((a, b) => +b.minutesInGamingRoom - +a.minutesInGamingRoom);

      setPlayerSummary(sorted);
    } catch (error) {
      if (playerPageNumber === 0) setPlayerSummary([]);
      errorToast(error);
    } finally {
      setPlayerLoading(false);
    }
  };

  const fetchAllPlayersWithPagination = async (fromSocket?: boolean) => {
    if (!fromSocket) setPlayerLoading(true);
    const limitPerPage: number = limit ?? PlayerPageLimit.LIMIT;
    const allFetched: PlayerSummary[] = [];

    try {
      let page: number = 0;
      let keepFetching: boolean = true;

      while (keepFetching) {
        const players: PlayerSummary[] = await getFormattedPlayersPage(
          page,
          limitPerPage,
        );

        allFetched.push(...players);

        if (players.length < limitPerPage) {
          keepFetching = false;
        } else {
          page += 1;
        }
      }

      const sorted: PlayerSummary[] = allFetched.sort(
        (a, b) => +b.minutesInGamingRoom - +a.minutesInGamingRoom,
      );

      setAllPlayers(sorted);
    } catch (error) {
      errorToast(error);
    } finally {
      setPlayerLoading(false);
    }
  };

  useEffect(() => {
    const shouldFetch: boolean = isSweepRoute || venueBranchIds.length > 0;
    if (!shouldFetch) return;

    if (playerPageNumber === undefined) {
      fetchAllPlayersWithPagination();
    } else {
      fetchPlayerData();
    }
  }, [playerPageNumber, venueBranchDependencyKey, limit]);

  return { playerSummary, playerLoading, decodedLoginData, allPlayers };
};
